import { useEffect, useState } from "react";

/**
 * Wraps the `beforeinstallprompt` event (Chrome/Edge on desktop, Chrome on
 * Android) so a component can show its own "Install app" control instead of
 * waiting on the browser's own address-bar icon.
 *
 * That event is unreliable enough that a button gated on it alone
 * effectively disappears for a lot of real visitors: Safari (desktop and
 * iOS) never fires it at all, Firefox doesn't support installing this kind
 * of app, and even Chrome/Edge can go a while without firing it on a tab
 * that was already open before the manifest/service worker were in place —
 * only a fresh navigation reliably re-checks installability. So the button
 * this hook backs is never hidden outright (short of the app already being
 * installed) — `canInstall` says whether a one-click native prompt is ready,
 * and `instructions` is always a ready fallback describing how to install by
 * hand on whatever browser this is, for when it isn't.
 */
function detectBrowser() {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  const isSafari = /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(ua);
  const isFirefox = /Firefox|FxiOS/.test(ua);
  const isEdge = /Edg\//.test(ua);
  const isChrome = /Chrome|CriOS/.test(ua) && !isEdge;

  if (isIOS && isSafari) return "ios-safari";
  if (isIOS) return "ios-other"; // Chrome/Firefox on iOS — all use Safari's engine, same limitation
  if (isAndroid) return "android"; // Chrome/Edge on Android both support the native prompt
  if (isFirefox) return "firefox-desktop";
  if (isSafari) return "macos-safari";
  if (isChrome || isEdge) return "desktop-chromium";
  return "other";
}

const INSTRUCTIONS = {
  "ios-safari": "Tap the Share icon, then “Add to Home Screen.”",
  "ios-other": "Open this page in Safari, tap Share, then “Add to Home Screen” — only Safari can install on iOS.",
  "android": "Tap the ⋮ menu in your browser, then “Install app” (or “Add to Home screen”).",
  "firefox-desktop": "Firefox on desktop can't install this app — open this page in Chrome or Edge instead.",
  "macos-safari": "Safari on Mac can't install this app — open this page in Chrome or Edge instead.",
  "desktop-chromium": "Click the install icon at the right of the address bar, or the ⋮ menu → “Install app.”",
  "other": "Look for “Install app” or “Add to Home Screen” in your browser's menu.",
};

export function useInstallPrompt() {
  const [deferredEvent, setDeferredEvent] = useState(null);
  const [installed, setInstalled] = useState(
    () =>
      typeof window !== "undefined" &&
      (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true)
  );
  const [browser] = useState(detectBrowser);

  useEffect(() => {
    const onPrompt = (event) => {
      event.preventDefault();
      setDeferredEvent(event);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredEvent(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!deferredEvent) return false;
    deferredEvent.prompt();
    const { outcome } = await deferredEvent.userChoice;
    setDeferredEvent(null);
    return outcome === "accepted";
  };

  return {
    canInstall: !!deferredEvent && !installed,
    installed,
    promptInstall,
    instructions: INSTRUCTIONS[browser] || INSTRUCTIONS.other,
  };
}
